import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

interface OpenServiceRequest {
  _id: string;
  status: string;
  serviceType: { id: string; name: string; category: string };
  address: { street: string; city: string; state: string; zipCode: string };
  frequency: string;
  requestedDate: string;
  scheduledDate?: string;
  customerName: string;
  customerEmail?: string;
  providerName: string;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All open' },
  { value: 'pending', label: 'Pending' },
  { value: 'bidding', label: 'Bidding' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'pending_cancellation', label: 'Pending cancellation' },
];

const AdminOpenServiceRequestsPage: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<OpenServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean;
    requestId: string | null;
    loading: boolean;
    data: { serviceRequest: any; bids: any[] } | null;
    error: string | null;
  }>({ open: false, requestId: null, loading: false, data: null, error: null });

  const fetchOpenServiceRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get('/admin/open-service-requests');
      if (response.success && response.data) {
        setRequests((response.data as any).serviceRequests || []);
      } else {
        setError('Failed to load open service requests');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load open service requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOpenServiceRequests();
  }, []);

  const openDetail = (requestId: string) => {
    setDetailDialog({ open: true, requestId, loading: true, data: null, error: null });
  };

  useEffect(() => {
    if (!detailDialog.open || !detailDialog.requestId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.get(`/service-requests/${detailDialog.requestId}`);
        if (cancelled) return;
        if (res.success && res.data) {
          setDetailDialog((prev) => ({
            ...prev,
            loading: false,
            data: {
              serviceRequest: (res.data as any).serviceRequest,
              bids: (res.data as any).bids || [],
            },
            error: null,
          }));
        } else {
          setDetailDialog((prev) => ({ ...prev, loading: false, error: 'Failed to load request details', data: null }));
        }
      } catch (err: any) {
        if (!cancelled) {
          setDetailDialog((prev) => ({
            ...prev,
            loading: false,
            error: err.response?.data?.error || err.message || 'Failed to load request details',
            data: null,
          }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [detailDialog.open, detailDialog.requestId]);

  const closeDetail = () => {
    setDetailDialog({ open: false, requestId: null, loading: false, data: null, error: null });
  };

  const filteredRequests =
    statusFilter === 'all'
      ? requests
      : requests.filter((r) => r.status === statusFilter);

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'pending':
        return 'info';
      case 'bidding':
        return 'primary';
      case 'accepted':
      case 'scheduled':
        return 'secondary';
      case 'in-progress':
        return 'warning';
      case 'pending_cancellation':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (d: string | undefined) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return d;
    }
  };

  const formatAddress = (addr: OpenServiceRequest['address']) => {
    if (!addr) return '—';
    return [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ') || '—';
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Open Service Requests
          </Typography>
          <Typography variant="body1" color="text.secondary">
            All customer service requests that are not yet completed or cancelled
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchOpenServiceRequests} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Requested</strong></TableCell>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>Address</strong></TableCell>
              <TableCell><strong>Service</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Scheduled</strong></TableCell>
              <TableCell><strong>Provider</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  {requests.length === 0
                    ? 'No open service requests.'
                    : `No open service requests with status "${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label || statusFilter}".`}
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((sr) => (
                <TableRow
                  key={sr._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openDetail(sr._id)}
                >
                  <TableCell>{formatDate(sr.requestedDate)}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">{sr.customerName}</Typography>
                      {sr.customerEmail && (
                        <Typography variant="caption" color="text.secondary">{sr.customerEmail}</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }} title={formatAddress(sr.address)}>
                    <Typography variant="body2" noWrap>{formatAddress(sr.address)}</Typography>
                  </TableCell>
                  <TableCell>{sr.serviceType?.name ?? sr.serviceType?.id ?? '—'}</TableCell>
                  <TableCell>
                    <Chip label={sr.status.replace(/_/g, ' ')} size="small" color={getStatusColor(sr.status)} />
                  </TableCell>
                  <TableCell>{formatDate(sr.scheduledDate)}</TableCell>
                  <TableCell>{sr.providerName || '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={detailDialog.open} onClose={closeDetail} maxWidth="sm" fullWidth>
        <DialogTitle>
          Service Request Details
          {detailDialog.data?.serviceRequest && (
            <Typography variant="caption" display="block" color="text.secondary" fontWeight="normal">
              #{detailDialog.data.serviceRequest._id?.slice(-8) || detailDialog.requestId}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailDialog.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {detailDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailDialog.error}
            </Alert>
          )}
          {!detailDialog.loading && detailDialog.data && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Customer</Typography>
              <Typography variant="body2" gutterBottom>
                {detailDialog.data.serviceRequest.customerId
                  ? `${(detailDialog.data.serviceRequest.customerId as any).firstName} ${(detailDialog.data.serviceRequest.customerId as any).lastName}`
                  : '—'}
              </Typography>
              {(detailDialog.data.serviceRequest.customerId as any)?.email && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {(detailDialog.data.serviceRequest.customerId as any).email}
                </Typography>
              )}
              {(detailDialog.data.serviceRequest.customerId as any)?.phone && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {(detailDialog.data.serviceRequest.customerId as any).phone}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Address</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {detailDialog.data.serviceRequest.address
                  ? `${detailDialog.data.serviceRequest.address.street}, ${detailDialog.data.serviceRequest.address.city}, ${detailDialog.data.serviceRequest.address.state} ${detailDialog.data.serviceRequest.address.zipCode}`
                  : '—'}
              </Typography>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Service</Typography>
              <Typography variant="body2" gutterBottom>
                {detailDialog.data.serviceRequest.serviceType?.name ?? detailDialog.data.serviceRequest.serviceType?.id ?? '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Property: {detailDialog.data.serviceRequest.propertyType || '—'} · Frequency: {detailDialog.data.serviceRequest.frequency || '—'}
              </Typography>
              <Box sx={{ mt: 1, mb: 2 }}>
                <Chip
                  label={(detailDialog.data.serviceRequest.status || '').replace(/_/g, ' ')}
                  size="small"
                  color={getStatusColor(detailDialog.data.serviceRequest.status)}
                />
              </Box>

              {detailDialog.data.serviceRequest.scheduledDate && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Scheduled</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {formatDate(detailDialog.data.serviceRequest.scheduledDate)}
                  </Typography>
                </>
              )}

              {detailDialog.data.serviceRequest.providerId && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Provider</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {(detailDialog.data.serviceRequest.providerId as any).businessName ||
                      `${(detailDialog.data.serviceRequest.providerId as any).firstName} ${(detailDialog.data.serviceRequest.providerId as any).lastName}`}
                  </Typography>
                </>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Bids ({detailDialog.data.bids.length})</Typography>
              {detailDialog.data.bids.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No bids yet</Typography>
              ) : (
                <List dense disablePadding>
                  {detailDialog.data.bids.map((bid: any) => (
                    <ListItem key={bid._id} divider>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight="medium">
                              {bid.providerId
                                ? (bid.providerId.businessName || `${bid.providerId.firstName} ${bid.providerId.lastName}`)
                                : 'Provider'}
                            </Typography>
                            <Chip label={bid.status} size="small" color={getStatusColor(bid.status)} />
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            ${typeof bid.price === 'number' ? bid.price.toFixed(2) : Number(bid.price).toFixed(2)} · {formatDate(bid.createdAt)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminOpenServiceRequestsPage;
